export type BrowserPushSubscription = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
};

export function supportsWebPush(): boolean {
  return window.isSecureContext
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

/**
 * 배포 번들에 박힌 공개키를 우선 사용하되, 발송 서버의 키와 다르면 구독을 만들지 않는다.
 * 두 키가 다르면 브라우저 구독은 만들어져도 백엔드가 그 구독으로 발송할 수 없다.
 */
export function resolveWebPushPublicKey(serverPublicKey?: string | null): string | null {
  const buildPublicKey = (import.meta.env.VITE_ANGELFLOW_WEB_PUSH_PUBLIC_KEY as string | undefined)?.trim();
  const backendPublicKey = serverPublicKey?.trim();
  if (buildPublicKey && backendPublicKey && buildPublicKey !== backendPublicKey) {
    throw new Error("프런트와 백엔드의 Web Push 공개키가 일치하지 않습니다. 배포 설정을 확인해 주세요.");
  }
  return buildPublicKey || backendPublicKey || null;
}

export async function currentWebPushSubscription(): Promise<PushSubscription | null> {
  if (!supportsWebPush()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration?.pushManager.getSubscription() ?? null;
}

export async function subscribeCurrentBrowser(publicKey: string): Promise<BrowserPushSubscription> {
  if (!supportsWebPush()) throw new Error("이 브라우저에서는 웹 푸시를 사용할 수 없습니다.");
  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("브라우저 알림 권한을 허용해야 웹 푸시를 받을 수 있습니다.");

  const registration = await navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`);
  const existing = await registration.pushManager.getSubscription();
  const applicationServerKey = decodeApplicationServerKey(publicKey);
  const reuseExisting = existing !== null
    && applicationServerKeysMatch(existing.options.applicationServerKey, applicationServerKey);
  if (existing && !reuseExisting) await existing.unsubscribe();
  const subscription = existing && reuseExisting
    ? existing
    : await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("브라우저의 푸시 구독 정보를 읽지 못했습니다.");
  }
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

export async function unsubscribeCurrentBrowser(): Promise<string | null> {
  const subscription = await currentWebPushSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

function decodeApplicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const binary = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function applicationServerKeysMatch(current: ArrayBuffer | null, expected: Uint8Array): boolean {
  if (!current) return false;
  const actual = new Uint8Array(current);
  return actual.length === expected.length && actual.every((byte, index) => byte === expected[index]);
}
